import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { getInitialLanguage } from '../utils/languageDetection';
import PromoBanner from '../components/PromoBanner';
import './LandingPage.css';

/**
 * ============================================
 * VIDEO CLIPS CONFIGURATION - EASILY EXCHANGEABLE
 * ============================================
 * 
 * To add your story video clips, replace the URLs below with your video file URLs.
 * Recommended video format: MP4 (short clips, 5-15 seconds each)
 * Recommended resolution: 720p or 1080p (vertical format looks best)
 */
const VIDEO_CLIPS = [
  { id: 1, url: 'https://customer-assets.emergentagent.com/job_login-refer-fix/artifacts/h47p0y3i_SC1.mp4' }, // Max and the Dragon
  { id: 2, url: 'https://customer-assets.emergentagent.com/job_login-refer-fix/artifacts/qa0oxnt6_SC2.mp4' }, // Luna in the Magic Forest
  { id: 3, url: 'https://customer-assets.emergentagent.com/job_login-refer-fix/artifacts/50k95zh9_SC3.mp4' }, // Space Adventures
  { id: 4, url: 'https://customer-assets.emergentagent.com/job_login-refer-fix/artifacts/behzm7a8_SC4.mp4' }, // Princess Emma
  { id: 5, url: 'https://customer-assets.emergentagent.com/job_login-refer-fix/artifacts/f78lqpt7_SC5.mp4' }, // The Brave Knight
  { id: 6, url: 'https://customer-assets.emergentagent.com/job_login-refer-fix/artifacts/g5vvklek_SC6.mp4' }, // Underwater Friends
];

const EXAMPLE_CHILD_IMAGE = '/landing-demo-child.jpg';
const EXAMPLE_COVER_IMAGE = '/landing-demo-cover.jpg';
const EXAMPLE_INSIDE_VIDEO = '/landing-demo-inside.mp4';
const EXAMPLE_INSIDE_POSTER = '/landing-demo-inside-poster.jpg';

// Placeholder story cards when videos aren't available
const PLACEHOLDER_STORIES_DE = [
  { title: 'Max und der Drache', emoji: '🐉', gradient: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)' },
  { title: 'Luna im Zauberwald', emoji: '🌲', gradient: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)' },
  { title: 'Die Weltraum-Abenteuer', emoji: '🚀', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { title: 'Prinzessin Emma', emoji: '👸', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { title: 'Der mutige Ritter', emoji: '🏰', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { title: 'Unterwasser-Freunde', emoji: '🐠', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }
];

const PLACEHOLDER_STORIES_EN = [
  { title: 'Max and the Dragon', emoji: '🐉', gradient: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)' },
  { title: 'Luna in the Magic Forest', emoji: '🌲', gradient: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)' },
  { title: 'Space Adventures', emoji: '🚀', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { title: 'Princess Emma', emoji: '👸', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { title: 'The Brave Knight', emoji: '🏰', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { title: 'Underwater Friends', emoji: '🐠', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState(getInitialLanguage());
  const exampleDemoRef = useRef(null);
  const [starterName, setStarterName] = useState('Emma');
  const [starterAge, setStarterAge] = useState('7');
  const [starterTheme, setStarterTheme] = useState('zauberwald');
  // Check if animation was already completed this session
  const [animationComplete, setAnimationComplete] = useState(() => {
    return sessionStorage.getItem('landingAnimationComplete') === 'true';
  });
  const videoRefs = useRef({});
  const [videosPreloaded, setVideosPreloaded] = useState(false);

  // Get placeholder stories based on language
  const PLACEHOLDER_STORIES = language === 'de' ? PLACEHOLDER_STORIES_DE : PLACEHOLDER_STORIES_EN;

  // Capture referral code from URL and store in localStorage
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('referral_code', refCode);
    }
  }, [searchParams]);

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('appLanguage', newLanguage);
  };

  // Preload videos for smooth intro animation
  useEffect(() => {
    const preloadVideos = async () => {
      const promises = VIDEO_CLIPS.filter(clip => clip.url).map(clip => {
        return new Promise((resolve) => {
          const video = document.createElement('video');
          video.preload = 'auto';
          video.muted = true;
          video.src = clip.url;
          video.onloadeddata = () => resolve(true);
          video.onerror = () => resolve(false);
          // Timeout after 3 seconds
          setTimeout(() => resolve(false), 3000);
        });
      });
      
      await Promise.all(promises);
      setVideosPreloaded(true);
    };
    
    preloadVideos();
  }, []);

  // Trigger animation after videos are preloaded
  useEffect(() => {
    // Skip animation if already completed this session
    if (sessionStorage.getItem('landingAnimationComplete') === 'true') {
      return;
    }
    
    // Trigger animation sequence (run only once per session)
    // Phase 1: Cards appear one by one in center (0-1.2s)
    // Phase 2: Cards fly to positions (after 1.8s)
    const timer = setTimeout(() => {
      setAnimationComplete(true);
      sessionStorage.setItem('landingAnimationComplete', 'true');
    }, 1800);
    
    return () => clearTimeout(timer);
  }, []);

  const handleVideoHover = (id, isHovering) => {
    const video = videoRefs.current[id];
    if (video) {
      if (isHovering) {
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    }
  };

  const scrollToExampleStory = () => {
    exampleDemoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const themeChoices = language === 'de'
    ? [
        { id: 'zauberwald', label: 'Zauberwald' },
        { id: 'weltraum', label: 'Weltraum' },
        { id: 'prinzessin', label: 'Prinzessin' },
        { id: 'ritter', label: 'Ritter' }
      ]
    : [
        { id: 'magic-forest', label: 'Magic Forest' },
        { id: 'space', label: 'Space' },
        { id: 'princess', label: 'Princess' },
        { id: 'knight', label: 'Knight' }
      ];

  const trustPoints = language === 'de'
    ? ['Ohne Kreditkarte starten', 'In 1-2 Minuten erstellt', 'Dein Kind als Hauptfigur']
    : ['Start without a credit card', 'Created in 1-2 minutes', 'Your child as the hero'];

  const starterThemeLabel = themeChoices.find((themeChoice) => themeChoice.id === starterTheme)?.label || themeChoices[0]?.label;

  const benefitCards = language === 'de'
    ? [
        {
          title: 'Kein Schreiben nötig',
          text: 'Name, Alter und Thema eingeben. StoryTales übernimmt Text, Bilder und Layout.'
        },
        {
          title: 'Kind bleibt wiedererkennbar',
          text: 'Gesicht, Haare und Ausdruck bleiben über Cover und Seiten hinweg konsistent.'
        },
        {
          title: 'Für Eltern sofort verständlich',
          text: 'Du siehst direkt, was du bekommst: ein fertiges illustriertes Kinderbuch.'
        }
      ]
    : [
        {
          title: 'No writing required',
          text: 'Enter name, age and theme. StoryTales handles text, images and layout.'
        },
        {
          title: 'Your child stays recognizable',
          text: 'Face, hair and expression stay consistent across cover and pages.'
        },
        {
          title: 'Instantly clear for parents',
          text: 'You immediately see the outcome: a finished illustrated children’s book.'
        }
      ];

  const testimonials = language === 'de'
    ? [
        {
          quote: 'Meine Tochter wollte sofort noch eine Geschichte sehen, weil sie sich selbst erkannt hat.',
          author: 'Nina, Mama von Emma'
        },
        {
          quote: 'Endlich eine AI-App, bei der man sofort versteht, was am Ende herauskommt.',
          author: 'David, Papa von Max'
        }
      ]
    : [
        {
          quote: 'My daughter instantly wanted another story because she recognized herself right away.',
          author: 'Nina, mom of Emma'
        },
        {
          quote: 'Finally an AI product where you immediately understand the end result.',
          author: 'David, dad of Max'
        }
      ];

  const stats = language === 'de'
    ? [
        { value: '1-2 Min', label: 'bis zur fertigen Geschichte' },
        { value: '2-10', label: 'Jahre ideal geeignet' },
        { value: 'Foto → Story', label: 'klarer Vorher/Nachher-Effekt' }
      ]
    : [
        { value: '1-2 min', label: 'to a finished story' },
        { value: '2-10', label: 'years old works best' },
        { value: 'Photo → Story', label: 'clear before/after moment' }
      ];

  const handleStartFree = () => {
    localStorage.setItem(
      'landingStoryDraft',
      JSON.stringify({
        child_name: starterName,
        age: Number(starterAge),
        theme: starterTheme,
        theme_label: starterThemeLabel,
        language
      })
    );
    navigate('/auth');
  };

  const handleOpenAuth = () => {
    navigate('/auth');
  };

  const exampleSteps = language === 'de'
    ? [
        {
          label: '01',
          title: 'Beispielkind anlegen',
          text: 'Foto oder Profil hochladen, Name und Alter festlegen.'
        },
        {
          label: '02',
          title: 'Geschichte wird erstellt',
          text: 'Text, Cover und Seiten entstehen in wenigen Augenblicken.'
        },
        {
          label: '03',
          title: 'Wiedererkennbar in jeder Szene',
          text: 'Haar, Gesichtszüge und Stimmung bleiben über die Geschichte hinweg konsistent.'
        }
      ]
    : [
        {
          label: '01',
          title: 'Create a sample child',
          text: 'Upload a photo or profile, then set the name and age.'
        },
        {
          label: '02',
          title: 'Story gets generated',
          text: 'Text, cover and illustrated pages appear within moments.'
        },
        {
          label: '03',
          title: 'Recognizable in every scene',
          text: 'Hair, facial features and mood stay consistent across the whole story.'
        }
      ];

  return (
    <div className="landing-hero-page">
      <PromoBanner language={language} onLanguageChange={handleLanguageChange} />
      
      {/* Header */}
      <header className="landing-header">
        <div className="header-spacer"></div>
        <Button 
          onClick={handleOpenAuth}
          variant="outline"
          className="landing-auth-button"
        >
          {language === 'de' ? 'Anmelden / Registrieren' : 'Login / Sign up'}
        </Button>
      </header>

      {/* Hero Section */}
      <main className="landing-hero-main">
        {/* Intro Animation: Cards appear in center one by one */}
        <div className={`intro-cards-container ${animationComplete ? 'intro-hidden' : ''}`}>
          {VIDEO_CLIPS.map((clip, index) => (
            <div 
              key={`intro-${clip.id}`}
              className="intro-card"
              style={{ 
                '--intro-delay': `${index * 0.2}s`,
                background: clip.url ? 'transparent' : PLACEHOLDER_STORIES[index]?.gradient
              }}
            >
              {clip.url ? (
                <video
                  src={clip.url}
                  muted
                  loop
                  playsInline
                  autoPlay
                  preload="auto"
                  webkit-playsinline="true"
                  x5-playsinline="true"
                  className="intro-video-element"
                  style={{ background: '#000' }}
                />
              ) : (
                <>
                  <span className="intro-card-emoji">{PLACEHOLDER_STORIES[index]?.emoji || '📖'}</span>
                  <span className="intro-card-title">{PLACEHOLDER_STORIES[index]?.title || 'Story'}</span>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Desktop: Cards positioned around hero (after intro animation) */}
        <div className={`cards-container ${animationComplete ? 'animation-complete' : ''}`}>
          {/* Left side cards */}
          <div className="cards-left">
            {VIDEO_CLIPS.slice(0, 3).map((clip, index) => (
              <div 
                key={clip.id}
                className={`story-card story-card-${index + 1}`}
                style={{ '--delay': `${index * 0.15}s` }}
                onMouseEnter={() => handleVideoHover(clip.id, true)}
                onMouseLeave={() => handleVideoHover(clip.id, false)}
              >
                {clip.url ? (
                  <video
                    ref={el => videoRefs.current[clip.id] = el}
                    src={clip.url}
                    muted
                    loop
                    playsInline
                    autoPlay
                    webkit-playsinline="true"
                    x5-playsinline="true"
                    className="video-element"
                    onLoadedData={(e) => e.target.play().catch(() => {})}
                  />
                ) : (
                  <div 
                    className="video-placeholder"
                    style={{ background: PLACEHOLDER_STORIES[index]?.gradient }}
                  >
                    <span className="placeholder-emoji">{PLACEHOLDER_STORIES[index]?.emoji || '📖'}</span>
                    <span className="placeholder-title">{PLACEHOLDER_STORIES[index]?.title || 'Story'}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right side cards */}
          <div className="cards-right">
            {VIDEO_CLIPS.slice(3, 6).map((clip, index) => (
              <div 
                key={clip.id}
                className={`story-card story-card-${index + 4}`}
                style={{ '--delay': `${(index + 3) * 0.15}s` }}
                onMouseEnter={() => handleVideoHover(clip.id, true)}
                onMouseLeave={() => handleVideoHover(clip.id, false)}
              >
                {clip.url ? (
                  <video
                    ref={el => videoRefs.current[clip.id] = el}
                    src={clip.url}
                    muted
                    loop
                    playsInline
                    autoPlay
                    className="video-element"
                  />
                ) : (
                  <div 
                    className="video-placeholder"
                    style={{ background: PLACEHOLDER_STORIES[index + 3]?.gradient }}
                  >
                    <span className="placeholder-emoji">{PLACEHOLDER_STORIES[index + 3]?.emoji || '📖'}</span>
                    <span className="placeholder-title">{PLACEHOLDER_STORIES[index + 3]?.title || 'Story'}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Center Content */}
        <div className="hero-center-content">
          {/* Centered Logo */}
          <div className="hero-logo" onClick={() => navigate('/')}>
            <img 
              src="/storytales-logo.png" 
              alt="StoryTales Logo" 
            />
          </div>
          <span className="hero-eyebrow">
            {language === 'de'
              ? 'Persönliche Kinderbücher in Minuten'
              : 'Personal storybooks in minutes'}
          </span>
          <h1 className="hero-headline">
            {language === 'de' 
              ? 'Dein Kind als Held in einem fertigen, illustrierten Kinderbuch' 
              : 'Personalized children’s books where your child becomes the hero'}
          </h1>
          <p className="hero-subheadline">
            {language === 'de'
              ? 'Name, Alter und Thema eingeben. StoryTales erstellt in 1-2 Minuten eine Geschichte mit Cover, Seiten und einem Kind, das wirklich wiedererkennbar bleibt.'
              : 'Enter name, age and theme. StoryTales creates a fully illustrated story in 1-2 minutes that visibly feels like your child.'}
          </p>
          <div className="hero-actions">
            <Button 
              onClick={handleStartFree}
              className="hero-cta-button"
            >
              <span className="cta-icon">✨</span>
              {language === 'de' ? 'Kostenlos starten' : 'Start free'}
            </Button>
            <Button
              variant="outline"
              onClick={scrollToExampleStory}
              className="hero-secondary-button"
            >
              {language === 'de' ? 'Beispielgeschichte ansehen' : 'View sample story'}
            </Button>
          </div>
          <div className="hero-trust-pills">
            {trustPoints.map((point) => (
              <span key={point} className="hero-trust-pill">{point}</span>
            ))}
          </div>
          <div className="hero-proof-row">
            <div className="hero-proof-item">
              <strong>{language === 'de' ? 'Foto oder Profil' : 'Photo or profile'}</strong>
              <span>{language === 'de' ? 'als Ausgangspunkt' : 'as the starting point'}</span>
            </div>
            <div className="hero-proof-item">
              <strong>{language === 'de' ? 'Cover + Seiten' : 'Cover + pages'}</strong>
              <span>{language === 'de' ? 'voll illustriert' : 'fully illustrated'}</span>
            </div>
            <div className="hero-proof-item">
              <strong>{language === 'de' ? 'Kind bleibt erkennbar' : 'Child stays recognizable'}</strong>
              <span>{language === 'de' ? 'über die ganze Story' : 'throughout the story'}</span>
            </div>
          </div>
          <div className="quick-start-card">
            <div className="quick-start-head">
              <span className="quick-start-kicker">
                {language === 'de' ? 'Schnellstart' : 'Quick start'}
              </span>
              <h2>
                {language === 'de'
                  ? 'Teste vor der Anmeldung, wie eure erste Geschichte aussehen würde'
                  : 'Preview your first story before you sign up'}
              </h2>
              <p>
                {language === 'de'
                  ? 'Gib nur drei Dinge ein. Danach führen wir dich direkt in die Anmeldung und übernehmen den Entwurf.'
                  : 'Preview what your first story could look like right away.'}
              </p>
            </div>
            <div className="quick-start-grid">
              <label className="quick-start-field">
                <span>{language === 'de' ? 'Name des Kindes' : 'Child name'}</span>
                <input value={starterName} onChange={(e) => setStarterName(e.target.value)} />
              </label>
              <label className="quick-start-field quick-start-age">
                <span>{language === 'de' ? 'Alter' : 'Age'}</span>
                <select value={starterAge} onChange={(e) => setStarterAge(e.target.value)}>
                  {['3', '4', '5', '6', '7', '8', '9'].map((ageOption) => (
                    <option key={ageOption} value={ageOption}>{ageOption}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="theme-pill-row">
              {themeChoices.map((themeChoice) => (
                <button
                  key={themeChoice.id}
                  type="button"
                  onClick={() => setStarterTheme(themeChoice.id)}
                  className={`theme-pill ${starterTheme === themeChoice.id ? 'active' : ''}`}
                >
                  {themeChoice.label}
                </button>
              ))}
            </div>
            <div className="quick-start-preview">
              <span className="quick-start-preview-chip">
                {language === 'de' ? `Kind: ${starterName}` : `Child: ${starterName}`}
              </span>
              <span className="quick-start-preview-chip">
                {language === 'de' ? `Alter: ${starterAge}` : `Age: ${starterAge}`}
              </span>
              <span className="quick-start-preview-chip">
                {language === 'de' ? `Thema: ${starterThemeLabel}` : `Theme: ${starterThemeLabel}`}
              </span>
            </div>
            <Button onClick={handleStartFree} className="quick-start-button">
              {language === 'de' ? `Erste Geschichte für ${starterName} starten` : `Start first story for ${starterName}`}
            </Button>
            <p className="quick-start-note">
              {language === 'de'
                ? 'Ohne Kreditkarte. Der Entwurf wird direkt für die Anmeldung übernommen.'
                : 'No credit card required. Your draft carries over into sign up.'}
            </p>
          </div>
        </div>

        {/* Mobile: Cards at bottom in horizontal row */}
        <div className={`mobile-cards-container ${animationComplete ? 'animation-complete' : ''}`}>
          {VIDEO_CLIPS.map((clip, index) => (
            <div 
              key={clip.id}
              className="mobile-story-card"
              style={{ '--delay': `${index * 0.1}s` }}
            >
              {clip.url ? (
                <video
                  ref={el => videoRefs.current[`mobile-${clip.id}`] = el}
                  src={clip.url}
                  muted
                  loop
                  playsInline
                  autoPlay
                  webkit-playsinline="true"
                  x5-playsinline="true"
                  className="video-element"
                  onLoadedData={(e) => e.target.play().catch(() => {})}
                />
              ) : (
                <div 
                  className="video-placeholder"
                  style={{ background: PLACEHOLDER_STORIES[index]?.gradient }}
                >
                  <span className="placeholder-emoji">{PLACEHOLDER_STORIES[index]?.emoji || '📖'}</span>
                  <span className="placeholder-title">{PLACEHOLDER_STORIES[index]?.title || 'Story'}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </main>

      <section className="proof-strip-section">
        <div className="proof-strip-shell">
          <div className="proof-stats-grid">
            {stats.map((stat) => (
              <div key={stat.label} className="proof-stat-card">
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
          <div className="benefit-grid">
            {benefitCards.map((benefit) => (
              <div key={benefit.title} className="benefit-card">
                <h3>{benefit.title}</h3>
                <p>{benefit.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="example-story-section" ref={exampleDemoRef}>
        <div className="example-story-shell">
          <div className="example-story-copy">
            <span className="example-kicker">
              {language === 'de' ? 'So funktioniert es' : 'How it works'}
            </span>
            <h2>
              {language === 'de'
                ? 'Von einem Beispielkind zu einer Geschichte, die sich wirklich persönlich anfühlt'
                : 'From a sample child to a story that truly feels personal'}
            </h2>
            <p>
              {language === 'de'
                ? 'Hier siehst du kurz, wie eine Geschichte entsteht und wie stark sich das Beispielkind in Cover und Szenen wiederfindet.'
                : 'Here you can see how a story comes together and how clearly the sample child shows up in the cover and scenes.'}
            </p>

            <div className="example-steps">
              {exampleSteps.map((step) => (
                <div key={step.label} className="example-step-card">
                  <span className="example-step-label">{step.label}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              ))}
            </div>

            <Button
              onClick={() => navigate('/demo-story')}
              className="example-story-button"
            >
              {language === 'de' ? 'Demo-Story öffnen' : 'Open demo story'}
            </Button>
          </div>

          <div className="example-story-visual">
            <div className="example-child-card">
              <div className="example-child-portrait">
                <img
                  src={EXAMPLE_CHILD_IMAGE}
                  alt={language === 'de' ? 'Beispielkind Emma' : 'Sample child Emma'}
                />
              </div>
              <div className="example-child-meta">
                <span className="example-child-tag">
                  {language === 'de' ? 'Beispielkind' : 'Sample child'}
                </span>
                <h3>Emma, 7</h3>
                <p>
                  {language === 'de'
                    ? 'Langes hellbraunes Haar, grün-graue Augen, ruhiger Blick, sanfte Gesichtszüge'
                    : 'Long light-brown hair, green-grey eyes, calm expression, soft facial features'}
                </p>
              </div>
            </div>

            <div className="example-creation-card">
              <div className="example-creation-top">
                <span className="creation-status-dot"></span>
                <span>
                  {language === 'de' ? 'Geschichte wird erstellt' : 'Story is being created'}
                </span>
              </div>
              <div className="creation-progress-track">
                <div className="creation-progress-fill"></div>
              </div>
              <p>
                {language === 'de'
                  ? 'Text, Cover und Seiten werden gerade aufgebaut.'
                  : 'Text, cover and pages are being assembled right now.'}
              </p>
            </div>

            <div className="example-story-results">
              <div className="example-result-card">
                <span className="example-result-label">
                  {language === 'de' ? 'Cover' : 'Cover'}
                </span>
                <img
                  src={EXAMPLE_COVER_IMAGE}
                  alt={language === 'de' ? 'Beispiel-Cover' : 'Sample cover'}
                  className="example-result-media"
                />
              </div>
              <div className="example-result-card">
                <span className="example-result-label">
                  {language === 'de' ? 'Innenseite' : 'Inside page'}
                </span>
                <video
                  src={EXAMPLE_INSIDE_VIDEO}
                  muted
                  loop
                  playsInline
                  autoPlay
                  poster={EXAMPLE_INSIDE_POSTER}
                  className="example-result-video"
                />
              </div>
            </div>

            <div className="example-consistency-note">
              {language === 'de'
                ? 'Die Beispielgeschichte zeigt dieselbe Figur konsistent über Cover und Szenen hinweg.'
                : 'The sample story keeps the same character recognizable across cover and scenes.'}
            </div>
          </div>
        </div>
      </section>

      <section className="testimonial-section">
        <div className="testimonial-shell">
          <div className="testimonial-copy">
            <span className="example-kicker">
              {language === 'de' ? 'Warum Eltern bleiben' : 'Why parents convert'}
            </span>
            <h2>
              {language === 'de'
                ? 'Eltern verstehen auf einen Blick, was sie bekommen'
                : 'Parents understand the value at a glance'}
            </h2>
            <p>
              {language === 'de'
                ? 'Nicht einfach nur “AI für Geschichten”, sondern ein klarer, sichtbarer Vorher/Nachher-Effekt: dein Kind wird zur Figur im Buch.'
                : 'Not just “AI for stories,” but a clear visible before/after moment: your child becomes the character in the book.'}
            </p>
            <div className="testimonial-copy-points">
              <span>{language === 'de' ? 'Klarer Outcome statt abstrakter AI' : 'Clear outcome instead of vague AI'}</span>
              <span>{language === 'de' ? 'Schneller erster Erfolg' : 'Fast first success moment'}</span>
              <span>{language === 'de' ? 'Hohe Wiedererkennbarkeit' : 'Strong visual resemblance'}</span>
            </div>
          </div>
          <div className="testimonial-grid">
            {testimonials.map((testimonial) => (
              <div key={testimonial.author} className="testimonial-card">
                <p>“{testimonial.quote}”</p>
                <span>{testimonial.author}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-links">
          <Link to="/contact">{language === 'de' ? 'Kontakt' : 'Contact'}</Link>
          <span className="footer-divider">•</span>
          <Link to="/impressum">Impressum</Link>
          <span className="footer-divider">•</span>
          <Link to="/agb">AGB</Link>
          <span className="footer-divider">•</span>
          <Link to="/datenschutz">{language === 'de' ? 'Datenschutz' : 'Privacy'}</Link>
        </div>
        <div className="footer-social">
          <a href="https://www.facebook.com/profile.php?id=61584845610389" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a>
          <a href="https://www.instagram.com/storytales_xyz/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          <a href="https://www.youtube.com/channel/UCfpMWdR9CJJZfN-MH9-dEgQ" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </a>
          <a href="https://www.tiktok.com/@storytales_xyz" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
            </svg>
          </a>
        </div>
      </footer>
    </div>
  );
}
